import { useMemo } from 'react'
import { Animated, Image, StyleSheet } from 'react-native'

import { useNavigation } from '@react-navigation/native'
import { PressableWithIntent } from '@zet/common/components/pressableWithIntent'
import { Icon, colors } from '@zet/design-system'
import { Stack, XStack } from '@zet/design-system/layout'
import { H4 } from '@zet/design-system/typography'
import { useTier } from '@zet/feature-loyalty/api/hooks'
import { useUserContext } from '@zet/feature-user'
import ExpertTag from '@zet/svg/assets/ExpertTag.svg'
import GeniusTag from '@zet/svg/assets/GeniusTag.svg'
import ProTag from '@zet/svg/assets/ProTag.svg'
import { BUTTON_TYPE, DEVICE_WIDTH } from '@zet/utils/Constants'
import { getName } from '@zet/utils/stringConversion'
import { StatusBar } from 'expo-status-bar'
import { styled } from 'nativewind'

import { i18n } from '../../localization'
import { EXPERT, GENIUS, PRO } from '../../utils/constants'
import HelpButton from './HelpButton'
import { NotifyIndicator } from './NotifyIndicator'

const HeaderWrapper = styled(XStack, 'justify-between w-full  py-3 px-3')

const ProfileContainer = styled(XStack, 'justify-center items-center')
const Initials = styled(
  Stack,
  " w-[44] h-[44] rounded-md bg-['#316D50'] justify-center items-center "
)
const NotificationContainer = styled(XStack, 'justify-center items-center')

export function HomeHeader({
  animatedFadeOut = false,
  animatedColor = false,
  animatedHelloColor = false,
  wrapperHeader = false,
  isSpotlightVisible = false,
  isNewUserTaskVisible = false,
  isDemoSaleTaskVisible = false,
}: {
  animatedFadeOut?: Animated.AnimatedInterpolation<string> | boolean
  animatedColor?: Animated.AnimatedInterpolation<string> | boolean
  animatedHelloColor?: Animated.AnimatedInterpolation<string> | boolean
  wrapperHeader?: boolean
  isNewUserTaskVisible?: boolean
  isSpotlightVisible?: boolean
  isDemoSaleTaskVisible?: boolean
}) {
  const { data } = useUserContext()
  const { data: tierData } = useTier()

  function handleNotificationColor() {
    if (!isNewUserTaskVisible && !isSpotlightVisible) {
      return colors.neutral.black100
    } else if (isSpotlightVisible || wrapperHeader) {
      return colors.neutral.white100
    } else {
      return colors.neutral.black100
    }
  }

  const navigation = useNavigation()

  const helpButtonColors = useMemo(() => {
    if (isSpotlightVisible || isNewUserTaskVisible) {
      return {
        bgStyles: 'bg-tertiary-90',
        textStyles: 'text-white',
        iconColor: colors.neutral.white100,
      }
    } else {
      return {
        bgStyles: 'bg-grays-20 border border-grays-30',
        textStyles: 'text-tertiary-200',
        iconColor: colors.tertiary[200],
      }
    }
  }, [isSpotlightVisible, isNewUserTaskVisible])

  const getTextColor = () => {
    if (animatedColor) return animatedColor
    else if (isSpotlightVisible) return colors.neutral.white100
    else if (isDemoSaleTaskVisible) return colors.grays[100]
    return 'black'
  }

  const getHelloTextColor = () => {
    if (animatedHelloColor) return animatedHelloColor
    else if (isSpotlightVisible) return colors.neutral.white100
    else if (isDemoSaleTaskVisible) return colors.grays[70]
    return 'black'
  }

  const navigateToNotifications = () =>
    navigation.navigate('Notifications' as never)
  const navigateToProfile = () => {
    navigation.navigate('Profile' as never)
  }

  const navigateToLeaderboard = () => {
    navigation.navigate('LeaderBoard' as never)
  }

  function renderTag(cohort: string) {
    switch (cohort) {
      case PRO:
        return <ProTag />
      case GENIUS:
        return <GeniusTag />
      case EXPERT:
        return <ExpertTag />
      default:
        return <></>
    }
  }

  return (
    <HeaderWrapper
      style={{
        width: DEVICE_WIDTH,
        ...(!!isSpotlightVisible && { borderBottomWidth: 0 }),
        ...(isNewUserTaskVisible && { borderBottomWidth: 0 }),
      }}
    >
      <ProfileContainer>
        <PressableWithIntent
          type={BUTTON_TYPE.PRESSABLE}
          name={'UserProfile'}
          onPress={navigateToProfile}
          eventName="click_action"
          eventPayload={{ item_name: 'home_page_profile_icon' }}
        >
          <Initials>
            {data?.profilePicURL ? (
              <Image
                source={{ uri: data?.profilePicURL }}
                style={styles.profilePic}
              />
            ) : (
              <H4 className="text-neutral-white100 ">
                {data?.fullName?.slice(0, 2).toUpperCase()}
              </H4>
            )}
          </Initials>
        </PressableWithIntent>
        <Stack className="ml-4/2">
          <Animated.Text
            className="text-md  font-[Graphie-SemiBold] leading-7"
            style={{
              color: getHelloTextColor(),
              marginBottom: -10,
            }}
            numberOfLines={1}
          >
            {i18n.t('joiningKit.hello')}
          </Animated.Text>
          <XStack className="items-center">
            <Animated.Text
              className="font-[Graphie-SemiBold]  text-lg leading-7"
              style={{
                color: getTextColor(),
              }}
              numberOfLines={1}
            >
              {getName(data?.fullName as string)}
            </Animated.Text>
            <Stack className="ml-2">{renderTag(tierData?.cohort)}</Stack>
          </XStack>
        </Stack>
      </ProfileContainer>
      <XStack className="items-center">
        <Animated.View style={{ opacity: animatedFadeOut || 1 }}>
          <HelpButton {...helpButtonColors} />
        </Animated.View>
        <NotificationContainer>
          {/* // Not needed now */}
          {/* {leaderboardData?.isLive && (
          <Pressable
            eventName="click_action"
            eventPayload={{ item_name: 'Leaderboard1' }}
            onPress={navigateToLeaderboard}
          >
            <PodiumOutlined className="mr-3" size={48} />
          </Pressable>
        )} */}
          <NotifyIndicator onPress={navigateToNotifications}>
            <Icon
              size={24}
              type="NotificationOutlined"
              color={handleNotificationColor()}
            />
          </NotifyIndicator>
          {/* //TODO: This is for story once this is in use uncomment this code  */}
          {/* {stories ? (
          <Pressable onPress={handlePress}>
            <GradientFrame className="ml-4 ">
              <LogoContainer>
                <Image
                  className=" h-[8] w-[20] "
                  resizeMode="contain"
                  source={require('../../assets/images/header/ZET.png')}
                />
              </LogoContainer>
            </GradientFrame>
          </Pressable>
        ) : (
          <LogoContainer className="ml-4 ">
            <Image
              className=" h-[8] w-[20] "
              resizeMode="contain"
              source={require('../../assets/images/header/ZET.png')}
            />
          </LogoContainer>
        )} */}
        </NotificationContainer>
      </XStack>
      <StatusBar backgroundColor={'#fff'} style="dark" />
    </HeaderWrapper>
  )
}

const styles = StyleSheet.create({
  profilePic: {
    width: 44,
    height: 44,
    borderRadius: 6,
  },
})
